import React from 'react';
import { BellIcon, UserIcon, MenuIcon, LogOutIcon, SearchIcon, HelpCircleIcon } from 'lucide-react';
import { UserRole, getRoleLabel } from '../../types/auth';
interface TopbarProps {
  toggleSidebar: () => void;
  collapsed: boolean;
  onLogout: () => void;
  userName: string;
  userRole: UserRole;
  userUnit: string;
}
export const Topbar: React.FC<TopbarProps> = ({
  toggleSidebar,
  collapsed,
  onLogout,
  userName,
  userRole,
  userUnit
}) => {
  return <header className="bg-dark-800 border-b border-dark-700 shadow-md z-20 relative" data-id="element-715">
      {/* Gold Accent Line */}
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/50 to-transparent" data-id="element-716"></div>

      <div className="flex items-center justify-between px-4 py-3" data-id="element-717">
        <div className="flex items-center min-w-0 flex-1" data-id="element-718">
          <button onClick={toggleSidebar} className="p-2 mr-3 rounded-md text-gray-400 hover:text-white hover:bg-dark-700 transition-colors flex-shrink-0 lg:hidden" aria-label="Toggle sidebar" data-id="element-719">
            <MenuIcon size={20} data-id="element-720" />
          </button>

          <div className="flex items-center min-w-0" data-id="element-721">
            <div className="relative group flex-shrink-0" data-id="element-722">
              <div className="absolute -inset-1 bg-primary/20 rounded-full blur opacity-0 group-hover:opacity-100 transition duration-500" data-id="element-723"></div>
              <img src="https://cdn.magicpatterns.com/uploads/hFLsea95KTbfu8th3Tcctj/logo-BIN-NEW.png" alt="Logo BIN" className="h-8 w-8 object-contain mr-3 relative transition-transform duration-300 group-hover:scale-105" data-id="element-724" />
            </div>
            <div className="flex flex-col min-w-0 border-l border-dark-600 pl-3 ml-1 flex-shrink-1" data-id="element-725">
              <h2 className="text-white font-bold text-sm sm:text-base truncate tracking-tight leading-tight" data-id="element-726">
                ARSENA{' '}
                <span className="text-primary font-normal text-xs ml-1 opacity-80" data-id="element-727">
                  INTELLIGENCE
                </span>
              </h2>
              <p className="text-[10px] text-gray-400 hidden sm:block truncate font-medium tracking-wide" data-id="element-728">
                Asset Resource Strategic Enterprise Network for
                Advanced-Technology
              </p>
            </div>
          </div>
        </div>

        {/* Center Search (Desktop) */}
        <div className="hidden md:flex flex-1 max-w-md mx-4" data-id="element-729">
          <div className="relative w-full group" data-id="element-730">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-731">
              <SearchIcon className="h-4 w-4 text-gray-500 group-focus-within:text-primary transition-colors" data-id="element-732" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-1.5 border border-dark-600 rounded-md leading-5 bg-dark-900/50 text-gray-300 placeholder-gray-500 focus:outline-none focus:bg-dark-900 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 sm:text-sm transition-all duration-200" placeholder="Cari aset, dokumen, atau personel..." data-id="element-733" />
            <div className="absolute inset-y-0 right-0 pr-2 flex items-center" data-id="element-734">
              <kbd className="inline-flex items-center border border-dark-600 rounded px-2 text-[10px] font-sans font-medium text-gray-500" data-id="element-735">
                ⌘K
              </kbd>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0" data-id="element-736">
          <button className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-dark-700 transition-colors" aria-label="Help" data-id="element-737">
            <HelpCircleIcon size={18} data-id="element-738" />
          </button>

          <button className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-dark-700 relative transition-colors group" aria-label="Notifications" data-id="element-739">
            <BellIcon size={18} className="group-hover:animate-swing" data-id="element-740" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-accent-red rounded-full ring-2 ring-dark-800 animate-pulse" data-id="element-741"></span>
          </button>

          <div className="h-6 w-px bg-dark-600 mx-2" data-id="element-742"></div>

          <div className="flex items-center pl-1 group cursor-pointer" data-id="element-743">
            <div className="mr-3 text-right hidden md:block" data-id="element-744">
              <p className="text-sm font-bold text-gray-200 group-hover:text-white transition-colors" data-id="element-745">
                {userName}
              </p>
              <div className="flex items-center justify-end gap-1.5" data-id="element-746">
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${userRole === 'super_admin' ? 'bg-primary' : 'bg-blue-500'}`} data-id="element-747"></span>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium" data-id="element-748">
                  {getRoleLabel(userRole)}
                </p>
              </div>
            </div>
            <div className="relative" data-id="element-749">
              <div className={`p-0.5 rounded-full border-2 ${userRole === 'super_admin' ? 'border-primary/50 group-hover:border-primary' : 'border-blue-500/50 group-hover:border-blue-500'} transition-colors duration-300`} data-id="element-750">
                <div className={`p-1.5 rounded-full ${userRole === 'super_admin' ? 'bg-primary text-dark-900' : 'bg-blue-500 text-white'}`} data-id="element-751">
                  <UserIcon size={16} data-id="element-752" />
                </div>
              </div>
              <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-dark-800 rounded-full" data-id="element-753"></div>
            </div>
          </div>

          <button onClick={onLogout} className="ml-2 p-2 rounded-md text-gray-400 hover:text-accent-red hover:bg-dark-700 transition-colors" title="Logout" aria-label="Logout" data-id="element-754">
            <LogOutIcon size={18} data-id="element-755" />
          </button>
        </div>
      </div>
    </header>;
};